import RouteError from '@/components/ui/route-error';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { SERVICES_TABS } from '@/types/route-tabs';
import { queryClient } from '@/lib/query-client';
import { useAppStore } from '@/stores/app-store';
import {
  getConfigQueryOptions,
  useWholesalerConfig,
  useUpdateWholesalerConfig,
} from '@/features/services/api/services-api';
import { memo, useState, useEffect, useRef, useMemo } from 'react';
import DashboardRouteLayout from '@/app/components/dashboard-route-layout';
import DashboardPageShell from '@/app/components/dashboard-page-shell';
import { Tabs } from '@/components/ui/lib/tabs';
import { Flex } from '@/components/ui/lib/flex';
import { Skeleton } from '@/components/ui/lib/skeleton';
import SaveButton from '@/components/ui/save-button';
import { DICTIONARY } from '@/config/dictionary';
import { requireAuth } from '@/utils/require-auth-before-load';
import ServicesTab from '@/features/services/components/services-tab';
import FeaturesTab from '@/features/services/components/features-tab';
import CarriageTab from '@/features/services/components/carriage-tab';
import { useWholesalerCurrent } from '@/features/payments/api/wholesaler-current-api';
import { isKycApproved } from '@/utils/kyc-status';
import type { CarriageOption, CountryCarriageConfig, ServiceVisibility } from '@/types/services';
import { REGIONS } from '@/config/regions';
import { getCountriesQueryOptions, useCountries } from '@/hooks/use-countries';
import {
  getServiceFeatureCatalogQueryOptions,
  useServiceFeatureCatalog,
} from '@/hooks/use-service-feature-catalog';
import {
  buildEnabledCountryCodeSet,
  filterRegionsByEnabledCountryCodes,
} from '@/utils/country-availability';
import type { AdminServiceVisibility } from '@/types/admin-service-visibility';
import {
  buildLockedByAdminVisibilityMap,
  filterOfferedByAdminVisibility,
  mapAdminVisibilityToServiceValue,
} from '@/utils/admin-service-visibility';

const ServicesTabPanel = memo(ServicesTab);
const FeaturesTabPanel = memo(FeaturesTab);
const CarriageTabPanel = memo(CarriageTab);

const VALID_CARRIAGE_OPTIONS = new Set<CarriageOption>(['pingco']);

const DashboardServicesPage = () => {
  const d = DICTIONARY.servicesPage;
  const { tab } = useSearch({ from: '/services' });

  const selectedWholesalerId = useAppStore((s) => s.selectedWholesalerId);
  const setSelectedWholesalerId = useAppStore((s) => s.setSelectedWholesalerId);
  const initialWholesalerId = useAppStore((s) => s.currentUser?.wholesalers[0]?.id ?? null);
  const wholesalerId = selectedWholesalerId ?? initialWholesalerId ?? '';

  useEffect(() => {
    if (selectedWholesalerId == null && initialWholesalerId != null) {
      setSelectedWholesalerId(initialWholesalerId);
    }
  }, [selectedWholesalerId, initialWholesalerId, setSelectedWholesalerId]);

  const { data: config, isLoading } = useWholesalerConfig(wholesalerId);
  const { data: catalog, isLoading: isCatalogLoading } = useServiceFeatureCatalog();
  const { mutate: updateConfig, isPending: isSaving } = useUpdateWholesalerConfig();
  const { data: countries } = useCountries();
  const { data: wholesalerCurrent } = useWholesalerCurrent();
  const kycActivated = isKycApproved(wholesalerCurrent?.kycStatus);

  // ── Draft state ───────────────────────────────────────────────────────────────
  //
  // Rule 15 note: these four fields mirror server data (`config`/`catalog`) into
  // local state rather than TanStack Form. A full conversion was evaluated and
  // deferred — the draft here isn't a flat form: it's two `Record<id, value>` maps
  // (visibility/enabled) plus two arrays, all cross-derived from three async
  // queries (wholesaler config, catalog, countries), with
  // dirty-checking that diffs against a live-recomputed server snapshot on every
  // render (not just the initial seed) and three additional effects (below) that
  // must keep correcting the draft as platform config changes even after the user
  // has started editing. Re-modeling that as TanStack Form state without a
  // behavior regression needs dedicated verification this task's scope doesn't
  // cover, so the fix applied instead is the safe minimal one: the seed effect is
  // guarded by `seededForRef` (a ref, not a dependency-array re-run) so it fires
  // exactly once per wholesaler — matching the ref-guard pattern documented in
  // src/hooks/use-bootstrap-auth.ts — instead of mirroring on every server-data
  // change.

  const [visibilityMap, setVisibilityMap] = useState<Record<string, ServiceVisibility>>({});
  const [enabledMap, setEnabledMap] = useState<Record<string, boolean>>({});
  const [carriagePreference, setCarriagePreference] = useState<CarriageOption[]>([]);
  const [countryCarriage, setCountryCarriage] = useState<CountryCarriageConfig[]>([]);

  const allCountryCodes = useMemo(
    () => REGIONS.flatMap((region) => region.countries.map((country) => country.code)),
    [],
  );

  // Until the country list arrives, show everything rather than an empty carriage table —
  // a country the platform disabled is filtered out as soon as the query resolves.
  const enabledCountryCodes = useMemo(
    () => (countries != null ? buildEnabledCountryCodeSet(countries) : new Set(allCountryCodes)),
    [allCountryCodes, countries],
  );

  const carriageRegions = useMemo(
    () => filterRegionsByEnabledCountryCodes(REGIONS, enabledCountryCodes),
    [enabledCountryCodes],
  );

  // Platform visibility travels on the catalog itself, so Service/Feature Admin decisions
  // reach this page without it reading anything under /admin.
  const adminServiceVisibilityMap = useMemo(() => {
    const map: Record<string, AdminServiceVisibility> = {};
    for (const service of catalog?.services ?? []) {
      map[service.id] = service.visibility ?? 'on';
    }
    return map;
  }, [catalog?.services]);

  const adminFeatureVisibilityMap = useMemo(() => {
    const map: Record<string, AdminServiceVisibility> = {};
    for (const feature of catalog?.features ?? []) {
      map[feature.id] = feature.visibility ?? 'on';
    }
    return map;
  }, [catalog?.features]);

  // `off` in Service/Feature Admin means the platform does not offer it — it leaves the list
  // rather than sitting in it switched off. The draft maps below still cover every catalog
  // entry, so a hidden one keeps saving as `off` instead of reverting to whatever the
  // wholesaler last chose.
  const visibleServices = useMemo(
    () => filterOfferedByAdminVisibility(catalog?.services ?? []),
    [catalog?.services],
  );

  const visibleFeatures = useMemo(
    () => filterOfferedByAdminVisibility(catalog?.features ?? []),
    [catalog?.features],
  );

  const lockedServiceMap = useMemo(
    () => buildLockedByAdminVisibilityMap(catalog?.services ?? []),
    [catalog?.services],
  );

  const lockedFeatureMap = useMemo(
    () => buildLockedByAdminVisibilityMap(catalog?.features ?? []),
    [catalog?.features],
  );

  // ── Dirty detection ───────────────────────────────────────────────────────────

  const serverServiceVisibilityById: Record<string, ServiceVisibility> = {};
  for (const cfg of config?.serviceConfigs ?? []) {
    serverServiceVisibilityById[cfg.serviceId] = cfg.visibility;
  }

  const serverFeatureEnabledById: Record<string, boolean> = {};
  for (const cfg of config?.featureConfigs ?? []) {
    serverFeatureEnabledById[cfg.featureId] = cfg.enabled;
  }

  const serverVisibilityMap: Record<string, ServiceVisibility> = {};
  for (const service of catalog?.services ?? []) {
    const adminVisibility = adminServiceVisibilityMap[service.id] ?? 'on';
    serverVisibilityMap[service.id] = mapAdminVisibilityToServiceValue(
      adminVisibility,
      serverServiceVisibilityById[service.id],
    );
  }

  const serverEnabledMap: Record<string, boolean> = {};
  for (const feature of catalog?.features ?? []) {
    serverEnabledMap[feature.id] = serverFeatureEnabledById[feature.id] ?? false;
  }

  const serverPreference = config?.carriagePreference ?? [];
  const serverCountryCarriage = config?.countryCarriage ?? [];

  const isServicesDirty =
    Object.keys(visibilityMap).length !== Object.keys(serverVisibilityMap).length ||
    Object.entries(visibilityMap).some(([id, v]) => serverVisibilityMap[id] !== v);

  const isFeaturesDirty =
    Object.keys(enabledMap).length !== Object.keys(serverEnabledMap).length ||
    Object.entries(enabledMap).some(([id, v]) => serverEnabledMap[id] !== v);

  const isCarriageDirty =
    JSON.stringify(carriagePreference) !== JSON.stringify(serverPreference) ||
    JSON.stringify(countryCarriage) !== JSON.stringify(serverCountryCarriage);

  const isDirty = isServicesDirty || isFeaturesDirty || isCarriageDirty;

  // Seed draft state on first load and re-seed when the wholesaler changes.
  // Guarded by a ref (not just the dependency array) so this never re-fires and
  // stomps on in-progress user edits once a wholesaler has been seeded.
  const seededForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!wholesalerId || !config || !catalog) return;
    if (seededForRef.current === wholesalerId) return;
    seededForRef.current = wholesalerId;

    const svcServerMap: Record<string, ServiceVisibility> = {};
    for (const cfg of config.serviceConfigs) svcServerMap[cfg.serviceId] = cfg.visibility;
    const svcMap: Record<string, ServiceVisibility> = {};
    for (const service of catalog.services) {
      const adminVisibility = adminServiceVisibilityMap[service.id] ?? 'on';
      svcMap[service.id] = mapAdminVisibilityToServiceValue(
        adminVisibility,
        svcServerMap[service.id],
      );
    }
    setVisibilityMap(svcMap);

    const ftServerMap: Record<string, boolean> = {};
    for (const cfg of config.featureConfigs) ftServerMap[cfg.featureId] = cfg.enabled;
    const ftMap: Record<string, boolean> = {};
    for (const feature of catalog.features) {
      ftMap[feature.id] = ftServerMap[feature.id] ?? false;
    }
    setEnabledMap(ftMap);

    setCarriagePreference(config.carriagePreference.filter((o) => VALID_CARRIAGE_OPTIONS.has(o)));
    setCountryCarriage(config.countryCarriage);
  }, [wholesalerId, config, catalog, adminServiceVisibilityMap, adminFeatureVisibilityMap]);

  // These are not seed-mirror effects (no re-seeding from `config` here) — they
  // enforce admin-visibility/country constraints on top of the already-seeded
  // draft as admin config changes, without overwriting the user's in-progress
  // edits to other fields. `hasChanges` short-circuits so they're no-ops once the
  // draft already satisfies the constraint.
  useEffect(() => {
    if (catalog == null) return;

    setVisibilityMap((prev) => {
      if (Object.keys(prev).length === 0) return prev;

      let hasChanges = false;
      const next: Record<string, ServiceVisibility> = { ...prev };

      for (const service of catalog.services) {
        const adminVisibility = adminServiceVisibilityMap[service.id] ?? 'on';
        const resolved = mapAdminVisibilityToServiceValue(adminVisibility, prev[service.id]);
        if (resolved !== prev[service.id]) {
          next[service.id] = resolved;
          hasChanges = true;
        }
      }

      return hasChanges ? next : prev;
    });
  }, [adminServiceVisibilityMap, catalog]);

  useEffect(() => {
    if (catalog == null) return;

    setEnabledMap((prev) => {
      if (Object.keys(prev).length === 0) return prev;

      let hasChanges = false;
      const next: Record<string, boolean> = { ...prev };

      for (const feature of catalog.features) {
        const adminVisibility = adminFeatureVisibilityMap[feature.id] ?? 'on';
        if (adminVisibility === 'off' || adminVisibility === 'disabled') {
          if (prev[feature.id] !== false) {
            next[feature.id] = false;
            hasChanges = true;
          }
        }
      }

      return hasChanges ? next : prev;
    });
  }, [adminFeatureVisibilityMap, catalog]);

  useEffect(() => {
    setCountryCarriage((prev) => {
      const next = prev.filter((configItem) => enabledCountryCodes.has(configItem.countryCode));
      return next.length === prev.length ? prev : next;
    });
  }, [enabledCountryCodes]);

  // ── Draft handlers ────────────────────────────────────────────────────────────

  const handleVisibilityChange = (serviceId: string, visibility: ServiceVisibility) => {
    if (lockedServiceMap[serviceId]) {
      return;
    }

    setVisibilityMap((prev) => ({ ...prev, [serviceId]: visibility }));
  };

  const handleFeatureToggle = (featureId: string, enabled: boolean) => {
    if (lockedFeatureMap[featureId]) {
      return;
    }

    setEnabledMap((prev) => ({ ...prev, [featureId]: enabled }));
  };

  const handleCarriagePreference = (vals: CarriageOption[]) => {
    setCarriagePreference(vals.filter((o) => VALID_CARRIAGE_OPTIONS.has(o)));
  };

  const handleCountryToggle = (countryCode: string, option: CarriageOption) => {
    setCountryCarriage((prev) => {
      const existing = prev.find((c) => c.countryCode === countryCode);
      if (!existing) {
        return [...prev, { countryCode, carriage: [option] }];
      }
      const alreadySelected = existing.carriage.includes(option);
      const newCarriage = alreadySelected
        ? existing.carriage.filter((o) => o !== option)
        : [...existing.carriage, option];
      if (newCarriage.length === 0) {
        return prev.filter((c) => c.countryCode !== countryCode);
      }
      return prev.map((c) => (c.countryCode === countryCode ? { ...c, carriage: newCarriage } : c));
    });
  };

  // ── Save ──────────────────────────────────────────────────────────────────────

  const handleSave = () => {
    if (!wholesalerId) return;

    updateConfig({
      wholesalerId,
      config: {
        serviceConfigs: Object.entries(visibilityMap).map(([serviceId, visibility]) => ({
          serviceId,
          visibility,
        })),
        featureConfigs: Object.entries(enabledMap).map(([featureId, enabled]) => ({
          featureId,
          enabled,
        })),
        carriagePreference,
        countryCarriage,
      },
    });
  };

  return (
    <DashboardRouteLayout>
      <DashboardPageShell title={d.heading} description={d.description}>
        <Flex justify="flex-end" mb="4" mt="-2">
          <SaveButton
            onClick={handleSave}
            isSaving={isSaving}
            disabled={!isDirty || !wholesalerId}
          />
        </Flex>

        <Tabs.Root
          defaultValue={tab ?? SERVICES_TABS[0]}
          variant="line"
          size="sm"
          lazyMount
          unmountOnExit
        >
          <Tabs.List mb="4">
            <Tabs.Trigger
              value={SERVICES_TABS[0]}
              _selected={{ fontWeight: 'semibold', color: 'accent' }}
            >
              {d.tabs.services}
            </Tabs.Trigger>
            <Tabs.Trigger
              value={SERVICES_TABS[1]}
              _selected={{ fontWeight: 'semibold', color: 'accent' }}
            >
              {d.tabs.features}
            </Tabs.Trigger>
            <Tabs.Trigger
              value={SERVICES_TABS[2]}
              _selected={{ fontWeight: 'semibold', color: 'accent' }}
            >
              {d.tabs.carriage}
            </Tabs.Trigger>
          </Tabs.List>

          {isLoading || isCatalogLoading || seededForRef.current !== wholesalerId ? (
            <Flex direction="column" gap="3">
              <Skeleton height="72px" rounded="2xl" />
              <Skeleton height="72px" rounded="2xl" />
              <Skeleton height="72px" rounded="2xl" />
              <Skeleton height="72px" rounded="2xl" />
            </Flex>
          ) : (
            <>
              <Tabs.Content value={SERVICES_TABS[0]}>
                <ServicesTabPanel
                  serviceCategories={catalog?.serviceCategories ?? []}
                  services={visibleServices}
                  visibilityMap={visibilityMap}
                  lockedServiceMap={lockedServiceMap}
                  onVisibilityChange={handleVisibilityChange}
                />
              </Tabs.Content>
              <Tabs.Content value={SERVICES_TABS[1]}>
                <FeaturesTabPanel
                  features={visibleFeatures}
                  baseDidPrice={catalog?.baseDidPrice ?? 0}
                  enabledMap={enabledMap}
                  lockedFeatureMap={lockedFeatureMap}
                  onToggle={handleFeatureToggle}
                />
              </Tabs.Content>
              <Tabs.Content value={SERVICES_TABS[2]}>
                <CarriageTabPanel
                  regions={carriageRegions}
                  preference={carriagePreference}
                  countryCarriage={countryCarriage}
                  onPreferenceChange={handleCarriagePreference}
                  onCountryToggle={handleCountryToggle}
                  kycActivated={kycActivated}
                />
              </Tabs.Content>
            </>
          )}
        </Tabs.Root>
      </DashboardPageShell>
    </DashboardRouteLayout>
  );
};

const servicesSearchSchema = z.object({
  tab: z.enum(SERVICES_TABS).optional(),
});

export const Route = createFileRoute('/services')({
  validateSearch: servicesSearchSchema,
  beforeLoad: requireAuth,
  loader: async () => {
    const { selectedWholesalerId, currentUser } = useAppStore.getState();
    const wholesalerId = selectedWholesalerId ?? currentUser?.wholesalers[0]?.id;
    if (wholesalerId == null) return null;
    await Promise.allSettled([
      queryClient.prefetchQuery(getConfigQueryOptions(wholesalerId)),
      queryClient.prefetchQuery(getCountriesQueryOptions()),
      queryClient.prefetchQuery(getServiceFeatureCatalogQueryOptions(wholesalerId)),
    ]);
    return null;
  },
  component: DashboardServicesPage,
  errorComponent: RouteError,
});
